import { fmtDateTime } from "@/components/show";
import {
  HeadToHeadCard,
  HighlightsCard,
  TopPerformersCard,
} from "@/pages/matches/components/details/match-extras-cards";
import {
  MatchLineupsCard,
  lineupsHaveData,
} from "@/pages/matches/components/details/match-lineups-card";
import {
  MatchReportCard,
  reportHasData,
} from "@/pages/matches/components/details/match-report-card";
import type { MatchSides } from "@/pages/matches/components/details/sides";
import { matchDetailsViewSchema } from "@/pages/matches/schemas/match-details.view";
import { DetailNotice } from "@/pages/sports/components/detail-notice";
import { AfterMatchNote } from "@/pages/sports/components/read-only-note";
import { SectionBoundary } from "@/pages/sports/components/section-boundary";
import { useSportsDetail } from "@/pages/sports/hooks/use-sports-detail";

/**
 * Everything TINA's follow-up jobs write about one match, read from
 * `GET /api/matches/{id}/details`: report, lineups, top performers, head-to-head, highlights
 * and when it was finalized. The document grows block by block, so each block renders only
 * when it has data; a match with no document yet gets one muted line. Each block sits in its
 * own boundary, so a wrongly typed value in one falls back to a muted line for that block only.
 */
export function MatchDetailsSection({
  matchId,
  sides,
}: {
  matchId: string;
  sides: MatchSides;
}) {
  return (
    <SectionBoundary key={matchId} what="match details">
      <MatchDetails matchId={matchId} sides={sides} />
    </SectionBoundary>
  );
}

function MatchDetails({
  matchId,
  sides,
}: {
  matchId: string;
  sides: MatchSides;
}) {
  const state = useSportsDetail(
    `matches/${matchId}/details`,
    matchDetailsViewSchema,
    "match details",
  );
  if (state.status !== "ready") {
    return (
      <DetailNotice
        state={state}
        what="match details"
        emptyText="No match details yet"
      />
    );
  }

  const {
    report,
    lineups,
    topPerformers,
    headToHead,
    highlights,
    finalizedAt,
  } = state.data;
  const showReport = reportHasData(report);
  const showLineups = lineupsHaveData(lineups);
  const showPerformers = !!topPerformers?.length;
  const showH2H =
    !!headToHead && (!!headToHead.played || !!headToHead.matches?.length);
  const showHighlights = !!highlights?.length;

  if (
    !showReport &&
    !showLineups &&
    !showPerformers &&
    !showH2H &&
    !showHighlights
  ) {
    return (
      <DetailNotice
        state={{ status: "empty" }}
        what="match details"
        emptyText="No match details yet"
      />
    );
  }

  return (
    <section className="flex flex-col gap-4">
      {finalizedAt != null && (
        <p className="text-sm text-muted-foreground">
          Finalized at {fmtDateTime(finalizedAt)}. Nothing more will change.
        </p>
      )}
      {showReport && report && (
        <SectionBoundary what="match report">
          <MatchReportCard report={report} sides={sides} />
        </SectionBoundary>
      )}
      {showLineups && lineups && (
        <SectionBoundary what="lineups">
          <MatchLineupsCard
            lineups={lineups}
            homeName={sides.homeName ?? "Home"}
            awayName={sides.awayName ?? "Away"}
          />
        </SectionBoundary>
      )}
      {showPerformers && topPerformers && (
        <SectionBoundary what="top performers">
          <TopPerformersCard performers={topPerformers} sides={sides} />
        </SectionBoundary>
      )}
      {showH2H && headToHead && (
        <SectionBoundary what="head-to-head">
          <HeadToHeadCard h2h={headToHead} sides={sides} />
        </SectionBoundary>
      )}
      {showHighlights && highlights && (
        <SectionBoundary what="highlights">
          <HighlightsCard clips={highlights} />
        </SectionBoundary>
      )}
      <AfterMatchNote />
    </section>
  );
}
